import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Button, Loader, MobileDrawerItem } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { FIRST_TIME_BADGE_DISABLED } from '../../../components/ViewControls/ColumnsFilter/constant';
import {
  clearCurrentPersonalization,
  createPersonalization,
  resetDynamicSortDetails,
  setGroupByOptions,
  setIsBulkActionsCompleted,
  setPersonalizationStatus,
  setTriggerForUserDefaultView,
  updatePersonalization,
} from '../../../redux/certdetails/action';
import {
  GROUP_BY_APP_VALUE,
  GROUP_BY_USER_VALUE,
  PERSONALIZATION_STATUS,
  VIEW_OPTIONS,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';

import ViewOptions from './ViewOptions';

import './CertsPersonalization.css';

function CertsPersonalization({
  isOpenViewOption,
  setIsOpenViewOption,
  isOnMobile = false,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [showLoaderOnSaveView, setShowLoaderOnSaveView] = useState(false);

  const { messages } = useSelector((state) => state?.user);
  const {
    bulkAction: { isBulkActionsCompleted } = {},
    personalization,
    viewControls: {
      advancedFilters,
      colsFilters,
      rowsFilters,
      groupByOptions,
    } = {},
  } = useSelector((state) => state?.certifications?.details || {});
  const personalizationViewOption = personalization?.viewOption?.view;

  const currentStatus = personalization?.status;
  const currentPersonalizatonApiStatus =
    personalization?.updatePersonalizatonApiStatus;
  const bulkActionsCompleted = isBulkActionsCompleted;

  const handleCreatePersonalization = () => {
    const selectedGroupByValue = groupByOptions?.find(
      (option) => option.isChecked
    )?.value;

    const payload = {
      defaultView: [
        {
          colsFilters,
          rowsFilters,
          advancedFilters,
        },
      ],
    };

    if (selectedGroupByValue === GROUP_BY_APP_VALUE) {
      payload.defaultView[0].groupByFilter = selectedGroupByValue;
    }

    dispatch(createPersonalization({ ...payload }));
  };

  const handleUpdatePersonalization = () => {
    const eTag = personalization?.personalizedData?.eTag;
    const selectedGroupByValue = groupByOptions?.find(
      (option) => option.isChecked
    )?.value;

    const payload = {
      defaultView: [
        {
          colsFilters,
          rowsFilters,
          advancedFilters,
        },
      ],
    };

    if (selectedGroupByValue === GROUP_BY_APP_VALUE) {
      payload.defaultView[0].groupByFilter = selectedGroupByValue;
    }

    dispatch(updatePersonalization(payload, eTag));
  };

  const handleSaveViewClick = () => {
    setShowLoaderOnSaveView(true);

    if (
      personalization?.personalizedData?.response ===
      PERSONALIZATION_STATUS.NO_VIEW_FOUND
    ) {
      handleCreatePersonalization();
    } else {
      handleUpdatePersonalization();
    }
  };

  const handleClearButtonClick = () => {
    const response = personalization?.personalizedData?.response;

    if (response === PERSONALIZATION_STATUS.NO_VIEW_FOUND) {
      dispatch(setPersonalizationStatus(''));
    } else {
      dispatch(setPersonalizationStatus('EXIST_PERSONALIZATION'));
    }

    // reset to default view
    if (personalizationViewOption === VIEW_OPTIONS.DEFAULT_VIEW) {
      dispatch(setTriggerForUserDefaultView(true));
    }

    //  to reset group by filter
    const personalizedViewData =
      personalization?.personalizedData?.result?.info?.defaultView;

    const existingView =
      personalizationViewOption === VIEW_OPTIONS.MY_PERSONALIZED_VIEW &&
      personalizedViewData?.[0]?.groupByFilter;
    const groupByValue = existingView
      ? personalizedViewData[0].groupByFilter
      : GROUP_BY_USER_VALUE;

    const updatedGroupByOptions = groupByOptions.map((option) => ({
      ...option,
      isChecked: option.value === groupByValue,
    }));

    dispatch(setGroupByOptions(updatedGroupByOptions));
    //  to reset group by filter
    FIRST_TIME_BADGE_DISABLED.count = 0;
    dispatch(resetDynamicSortDetails());
    dispatch(clearCurrentPersonalization(true));
    dispatch(setIsBulkActionsCompleted(false));
  };

  useEffect(() => {
    if (currentStatus === 'UPDATE_PERSONALIZATION') {
      dispatch(clearCurrentPersonalization(false));
    }

    if (
      currentStatus === 'EXIST_PERSONALIZATION' ||
      currentStatus === '' ||
      currentPersonalizatonApiStatus === ''
    ) {
      setShowLoaderOnSaveView(false);
    }
  }, [currentStatus, currentPersonalizatonApiStatus]);

  if (
    currentStatus === 'UPDATE_PERSONALIZATION' ||
    currentStatus === 'UPDATE_PERSONALIZATION_FAILED'
  ) {
    if (isOnMobile) {
      const options = [
        {
          label:
            messages.CERTS_SAVE_VIEW ||
            localize(intl, getMessageKey('CERTS_SAVE_VIEW')),
          value: 'saveView',
          isChecked: false,
          isDisabled: false,
          prefixIcon: 'SaveView',
          onClick: handleSaveViewClick,
        },
        {
          label:
            messages.CERTS_CLEAR ||
            localize(intl, getMessageKey('CERTS_CLEAR')),
          value: 'clear',
          isChecked: false,
          isDisabled: false,
          prefixIcon: 'Reject',
          onClick: handleClearButtonClick,
        },
      ];

      return (
        <>
          {options.map((option) => (
            <MobileDrawerItem
              option={option}
              onClick={option.onClick}
              suffixIcon={
                option.value === 'saveView' && showLoaderOnSaveView ? (
                  <Loader kind='dots' format='inline' color='brand' />
                ) : null
              }
            />
          ))}
        </>
      );
    }

    return (
      <>
        <Button
          type='button'
          kind='ghost'
          size='medium'
          className='CertPersonalization--clearButton'
          onClick={handleClearButtonClick}>
          {messages.CERTS_CLEAR || localize(intl, getMessageKey('CERTS_CLEAR'))}
        </Button>
        <Button
          type='button'
          kind='filled'
          size='medium'
          className='CertsHeader-button'
          onClick={handleSaveViewClick}
          isDisabled={bulkActionsCompleted}
          isLoading={showLoaderOnSaveView}
          loaderText={
            messages.CERTS_SAVE_VIEW ||
            localize(intl, getMessageKey('CERTS_SAVE_VIEW'))
          }>
          {messages.CERTS_SAVE_VIEW ||
            localize(intl, getMessageKey('CERTS_SAVE_VIEW'))}
        </Button>
      </>
    );
  }

  const personalizationInfo = personalization?.personalizedData?.result?.info;

  if (
    currentStatus === 'EXIST_PERSONALIZATION' &&
    personalizationInfo &&
    Object.keys(personalizationInfo).length > 0
  ) {
    return (
      <ViewOptions
        isOpenViewOption={isOpenViewOption}
        setIsOpenViewOption={setIsOpenViewOption}
        isOnMobile={isOnMobile}
      />
    );
  }

  return '';
}

CertsPersonalization.defaultProps = { isOnMobile: false };

CertsPersonalization.propTypes = {
  isOpenViewOption: PropTypes.bool.isRequired,
  setIsOpenViewOption: PropTypes.func.isRequired,
  isOnMobile: PropTypes.bool,
};

export default CertsPersonalization;
